import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Divider, Form, Input, Upload, UploadFile } from 'antd';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { FormAlert, useForm, useFormAlert } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	FileData,
	SiteReportBusinessActivity,
} from '@/libs/fleet-api/services/types';
import { useFRMSiteVisitReportPage } from './SiteReportPage';
import { uploader } from '@/libs/fleet-api/uploader';

type SiteReportBusinessActivityForm = SiteReportBusinessActivity<'payload'> &
	Pick<SiteReportBusinessActivity, 'supportingDocument'>;

export const useFRMSiteVisitReportActivityForm = () => {
	return useForm<SiteReportBusinessActivityForm>();
};

type FRMSiteVisitReportActivityProps = {
	onSubmit: (values: SiteReportBusinessActivityForm) => void;
};
const FRMSiteVisitReportActivity: FC<FRMSiteVisitReportActivityProps> = ({
	onSubmit,
}) => {
	const { query, siteReportActivityForm } = useFRMSiteVisitReportPage();
	const { siteReport, surveyInformation, mainInformation } =
		query.data?.data ?? {};
	const { form, fieldName } = siteReportActivityForm;

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const { alerts } = useFormAlert(form, null, false);

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			labelCol={{
				span: 10,
			}}
			initialValues={
				{
					id: siteReport?.id,
					surveyId: surveyInformation!.id,
					businessActivityVerification:
						siteReport?.businessActivityVerification,
					productionDataVsTargetVerification:
						siteReport?.productionDataVsTargetVerification,
					operationalConstraintsExplanation:
						siteReport?.operationalConstraintsExplanation,
					additionalBusinessActivityInfo:
						siteReport?.additionalBusinessActivityInfo,
					supportingDocument: siteReport?.supportingDocument,
				} as SiteReportBusinessActivityForm
			}
			onFinish={onSubmit}
		>
			<Form.Item
				name={fieldName('id')}
				noStyle
			/>
			<Form.Item
				name={fieldName('surveyId')}
				noStyle
			/>
			<Form.Item
				name={fieldName('businessActivityVerification')}
				label="Verifikasi Aktivitas Usaha"
				labelCol={{
					span: 6,
				}}
				rules={[rule.required('Verifikasi Aktivitas Usaha wajib diisi')]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('productionDataVsTargetVerification')}
				label="Verifikasi Data Produksi vs Target"
				labelCol={{
					span: 6,
				}}
				rules={[
					rule.required('Verifikasi Data Produksi vs Target wajib diisi'),
				]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('operationalConstraintsExplanation')}
				label="Penjelasan Hambatan Operasional"
				labelCol={{
					span: 6,
				}}
				rules={[rule.required('Penjelasan Hambatan Operasional wajib diisi')]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('additionalBusinessActivityInfo')}
				label="Informasi Lainnya"
				labelCol={{
					span: 6,
				}}
				rules={[rule.required('Informasi Lainnya wajib diisi')]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Divider />
			<Form.Item
				name={fieldName('supportingDocument')}
				label="Upload Dokumen Pendukung"
				labelCol={{ span: 6 }}
				rules={[rule.required('Upload Dokumen Pendukung wajib diisi')]}
				getValueProps={(value) => {
					const file = value as FileData;
					return {
						fileList: file?.id
							? ([
									{
										uid: file.id,
										name: file.name,
										thumbUrl: file.url,
									},
								] as UploadFile[])
							: [],
					};
				}}
				normalize={({ file }: { file: UploadFile }) => {
					return {
						id: file.uid,
						format: file.type,
						name: file.name,
						url: file.thumbUrl,
					} as FileData;
				}}
			>
				<Upload.Dragger
					listType="picture"
					accept=".pdf,.jpg,.jpeg,.png"
					maxCount={1}
					beforeUpload={() => false}
					onRemove={() => {
						form.setFieldValue(fieldName('supportingDocument'), null);
						return false;
					}}
					onChange={async ({ file }) => {
						const fileid = Date.now().toString();
						const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/supporting-document`;

						try {
							const url = await uploadMutation.mutateAsync({
								file: file as unknown as File,
								fileid,
								filepath,
							});

							form.setFieldValue(fieldName('supportingDocument'), {
								format: 'jpeg',
								id: fileid,
								name: file.name,
								path: filepath,
								url,
							});
						} catch {
							form.setFieldValue(fieldName('supportingDocument'), null);
						}
					}}
				>
					{uploadMutation.isPending ? (
						<LoadingOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					) : (
						<UploadOutlined
							style={{
								fontSize: 25,
								color: '#BABABA',
								display: 'block',
							}}
						/>
					)}
				</Upload.Dragger>
			</Form.Item>
			<Divider />
			<FormAlert alerts={alerts} />
		</Form>
	);
};

export default FRMSiteVisitReportActivity;
